import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button, ProgressBar, Badge } from "react-bootstrap";
import { BiRefresh, BiListUl, BiCategory, BiStore, BiCart } from "react-icons/bi";
import { getListingsDashboard } from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsDoughnut from "../Components/Charts/EChartsDoughnut";
import EChartsHorizontalBar from "../Components/Charts/EChartsHorizontalBar";
import moment from "moment";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const statusBadgeVariant = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "live") return "success";
  if (s === "draft" || s === "pending") return "warning";
  if (s === "under review" || s === "review") return "info";
  if (s === "inactive" || s === "deleted") return "danger";
  return "secondary";
};

const ListingsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getListingsDashboard({ timePeriod, limit: 20 });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalListings = useMemo(
    () => (data?.categoryWiseListings || []).reduce((sum, c) => sum + Number(c.listing_count), 0),
    [data]
  );

  const totalShops = useMemo(
    () => data?.shopWiseListingCount?.length || 0,
    [data]
  );

  const totalCategories = useMemo(
    () => data?.categoryWiseListings?.length || 0,
    [data]
  );

  const topByOrders = useMemo(
    () => data?.listingsByOrderCount?.[0]?.order_count || 0,
    [data]
  );

  const categoryChartData = useMemo(() => {
    if (!data?.categoryWiseListings) return {};
    const chartData = {};
    data.categoryWiseListings.slice(0, 8).forEach((c) => {
      chartData[c.category_name] = Number(c.listing_count);
    });
    return chartData;
  }, [data]);

  const shopBarData = useMemo(() => {
    if (!data?.shopWiseListingCount) return [];
    return data.shopWiseListingCount.slice(0, 10).map((s) => ({
      label: s.shop_name,
      value: Number(s.listing_count),
    }));
  }, [data]);

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Listings Dashboard</h5>
          <p className="text-muted mb-0">
            Recent listings, category-wise listings, shop-wise listing counts, and listings by order count.
          </p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={fetchData}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Listings"
                count={formatNumber(totalListings)}
                icon={<BiListUl />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Categories"
                count={formatNumber(totalCategories)}
                icon={<BiCategory />}
                color={ECHART_COLORS.accent}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Active Shops"
                count={formatNumber(totalShops)}
                icon={<BiStore />}
                color={ECHART_COLORS.success}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Top Orders"
                count={formatNumber(topByOrders)}
                icon={<BiCart />}
                color={ECHART_COLORS.purple}
              />
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Category Distribution</h6>
                <div style={{ height: 300 }}>
                  <EChartsDoughnut data={categoryChartData} />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Top 10 Shops by Listing Count</h6>
                <div style={{ height: 300 }}>
                  <EChartsHorizontalBar data={shopBarData} label="Listings" multiColor />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Recent Listings Table */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Recent Listings</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th style={{ width: 56 }}></th>
                        <th>Title</th>
                        <th>Shop</th>
                        <th>Category</th>
                        <th className="text-end">Price</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentListings.map((l, i) => (
                        <tr key={l.listing_id}>
                          <td className="text-center text-muted">{i + 1}</td>
                          <td>
                            <img
                              src={l.primary_image ? `${process.env.REACT_APP_URL_S3}/${l.primary_image}` : ""}
                              alt="listing"
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }}
                            />
                          </td>
                          <td>
                            <div className="fw-medium">{l.listing_title}</div>
                            <small className="text-muted">ID: {l.listing_id}</small>
                          </td>
                          <td>{l.shop_name}</td>
                          <td>{l.category_name}</td>
                          <td className="text-end">
                            {l.effective_price
                              ? `Rs. ${Number(l.effective_price).toLocaleString()}`
                              : "—"}
                          </td>
                          <td>
                            <Badge bg={statusBadgeVariant(l.status)} pill>
                              {l.status || "—"}
                            </Badge>
                          </td>
                          <td>{l.created_at ? moment(l.created_at).format("DD MMM YYYY") : "—"}</td>
                        </tr>
                      ))}
                      {data.recentListings.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-3">
                            No recent listings found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Listings by Order Count with Progress Bars */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Listings by Order Count</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Title</th>
                        <th>Shop</th>
                        <th className="text-end" style={{ width: 100 }}>Orders</th>
                        <th className="text-end" style={{ width: 100 }}>Qty Sold</th>
                        <th style={{ width: 180 }} title="Order count relative to the top-listed item">
                          % of Top <span className="text-muted fw-normal">(orders)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.listingsByOrderCount.map((l, i) => {
                        const maxOrders = topByOrders || 1;
                        const pct = Math.round((Number(l.order_count) / maxOrders) * 100);
                        return (
                          <tr key={l.listing_id}>
                            <td className="text-center text-muted">{i + 1}</td>
                            <td>
                              <div className="fw-medium">{l.listing_title}</div>
                              <small className="text-muted">ID: {l.listing_id}</small>
                            </td>
                            <td>{l.shop_name}</td>
                            <td className="text-end fw-bold">{formatNumber(l.order_count)}</td>
                            <td className="text-end">{formatNumber(l.quantity_sold)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar
                                  now={pct}
                                  variant={pct >= 75 ? "success" : pct >= 40 ? "primary" : "info"}
                                  style={{ height: 6, flex: 1 }}
                                />
                                <small className="text-muted" style={{ minWidth: 36 }}>{pct}%</small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {data.listingsByOrderCount.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-3">
                            No order data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default ListingsDashboard;
